import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Switch } from '@nyce/admin-kit/ui';
import { db, isActive, type DbServerRef, type Job, type LogLine } from '../../api/db';
import { EmptyState, ErrorBox, Loading, Page, useAsync } from '../../components/common';
import { client } from '../../session';
import { elapsedMs, formatBytes, formatDateTime, formatDuration } from '../../lib/format';
import { followJob, type StreamFn } from './jobStream';
import {
  Bar,
  canRunKind,
  dbErrorText,
  startDownload,
  StatusChip,
  Target,
  useNow,
  dbScopes,
  useSortedServers,
  type DbScopes,
} from './shared';

/** Keep the browser responsive on chatty jobs (pg_restore -v): only the newest lines are kept on screen. */
const MAX_LINES = 20_000;

/** Where a job's page is; the Databases page opens it for a started or picked job. */
export const jobPath = (id: number) => `/databases/jobs/${id}`;

/** One job (`/databases/jobs/:id`): status, facts, cancel/download and the live log. */
export function JobPage({ scopes, stream }: { scopes: readonly string[]; stream?: StreamFn }) {
  const { id } = useParams();
  const jobId = Number(id);
  const list = useAsync(() => db.servers(), []);
  const servers = useSortedServers(list.data);
  if (!Number.isInteger(jobId) || jobId <= 0)
    return (
      <Page title={<JobTitle jobId={null} job={null} />}>
        <EmptyState card title="Not a job id." />
      </Page>
    );
  return <JobView key={jobId} jobId={jobId} servers={servers} scopes={dbScopes(scopes)} stream={stream} />;
}

function JobTitle({ jobId, job }: { jobId: number | null; job: Job | null }) {
  return (
    <span className="dbx-drawer-title">
      <Link to="/databases?tab=jobs" className="nyk-back">
        Databases
      </Link>
      <span className="nyk-crumb-sep"> / </span>
      {jobId != null ? `Job #${jobId}` : 'Job'}
      {job && <StatusChip job={job} />}
    </span>
  );
}

function JobView({
  jobId,
  servers,
  scopes,
  onChanged,
  stream,
}: {
  jobId: number;
  servers: DbServerRef[];
  scopes: DbScopes;
  /** The job's status changed. */
  onChanged?: (job: Job) => void;
  stream?: StreamFn;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [mode, setMode] = useState<'stream' | 'poll' | 'done'>('stream');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [follow, setFollow] = useState(true);
  const [copied, setCopied] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const changed = useRef(onChanged);
  useEffect(() => {
    changed.current = onChanged;
  });
  const now = useNow(1000, !!job && isActive(job.status));

  useEffect(() => {
    const ctrl = new AbortController();
    let status: Job['status'] | null = null;
    const apply = (j: Job) => {
      setJob(j);
      // progress ticks do not concern the list; status changes do
      if (status !== null && status !== j.status) changed.current?.(j);
      status = j.status;
    };
    db.jobs.get(jobId).then(
      (j) => {
        if (ctrl.signal.aborted) return;
        setJob((cur) => cur ?? j);
        status ??= j.status;
      },
      (e) => !ctrl.signal.aborted && setError(dbErrorText(e))
    );
    void followJob({
      client,
      jobId,
      after: 0,
      signal: ctrl.signal,
      stream,
      onLog: (add) =>
        setLines((cur) =>
          cur.length + add.length > MAX_LINES ? [...cur, ...add].slice(-MAX_LINES) : [...cur, ...add]
        ),
      onJob: apply,
      onMode: setMode,
    });
    return () => ctrl.abort();
  }, [jobId, stream]);

  useLayoutEffect(() => {
    const el = logRef.current;
    if (follow && el) el.scrollTop = el.scrollHeight;
  }, [lines, follow]);

  const cancel = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const j = await db.jobs.cancel(jobId);
      setJob(j);
      onChanged?.(j);
    } catch (e) {
      setActionError(dbErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const r = await db.jobs.download(jobId);
      startDownload(r.url, job?.object?.split('/').pop() ?? '');
    } catch (e) {
      setActionError(dbErrorText(e));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    const text = lines.map((l) => `${l.at} [${l.stream}] ${l.line}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Could not copy to the clipboard.');
    }
  };

  const canCancel = !!job && isActive(job.status) && canRunKind(scopes, job.kind);
  const canDownload =
    !!job && job.kind === 'backup' && job.status === 'succeeded' && !!job.object && scopes.backup;
  const duration = job ? elapsedMs(job.startedAt, job.finishedAt, now) : null;

  return (
    <Page
      title={<JobTitle jobId={jobId} job={job} />}
      actions={
        <>
          {canCancel && (
            <Button size="sm" variant="danger" loading={busy} onClick={() => void cancel()}>
              Cancel job
            </Button>
          )}
          {canDownload && (
            <Button size="sm" variant="secondary" loading={busy} onClick={() => void download()}>
              Download
            </Button>
          )}
        </>
      }
    >
      <div className="dbx-job nyk-card" data-testid="job-page">
        <ErrorBox error={error} />
        <ErrorBox error={actionError} />
        <Loading loading={!job && !error} />
        {job && (
          <>
            {job.status === 'running' && (
              <Bar
                parts={[{ value: job.progress ?? 0 }]}
                max={100}
                label={job.progress != null ? `${Math.round(job.progress)}%` : 'running'}
              />
            )}
            <dl className="dbx-facts">
              <dt>Kind</dt>
              <dd>
                {job.kind} <span className="dbx-muted">({job.method})</span>
              </dd>
              <dt>{job.kind === 'copy' ? 'Source' : 'Database'}</dt>
              <dd>
                <Target servers={servers} server={job.serverId} database={job.database} />
              </dd>
              {job.targetServerId && (
                <>
                  <dt>Target</dt>
                  <dd>
                    <Target
                      servers={servers}
                      server={job.targetServerId}
                      database={job.targetDatabase ?? ''}
                    />
                  </dd>
                </>
              )}
              {job.object && (
                <>
                  <dt>Object</dt>
                  <dd className="nyk-url">
                    {job.object}
                    {job.sizeBytes != null && (
                      <span className="dbx-muted"> · {formatBytes(job.sizeBytes)}</span>
                    )}
                  </dd>
                </>
              )}
              <dt>Created</dt>
              <dd>
                {formatDateTime(job.createdAt)} <span className="dbx-muted">by {job.createdBy}</span>
                {job.scheduleId && <span className="dbx-muted"> · schedule {job.scheduleId}</span>}
              </dd>
              <dt>Started</dt>
              <dd>{formatDateTime(job.startedAt)}</dd>
              <dt>Finished</dt>
              <dd>
                {formatDateTime(job.finishedAt)}
                {duration != null && <span className="dbx-muted"> · {formatDuration(duration)}</span>}
              </dd>
            </dl>
            {job.error && (
              <div className="dbx-job-error" role="alert">
                {job.error}
              </div>
            )}
          </>
        )}
        <div className="dbx-log-bar">
          <span className="nyk-ui-eyebrow">Log</span>
          <span className="dbx-muted" data-testid="log-mode">
            {mode === 'stream' ? 'live' : mode === 'poll' ? 'polling (stream unavailable)' : 'complete'} ·{' '}
            {lines.length} lines
          </span>
          <span className="dbx-spacer" />
          <Switch checked={follow} onChange={setFollow} label="Auto-scroll" />
          <Button size="sm" variant="ghost" onClick={() => void copy()} disabled={!lines.length}>
            {copied ? 'Copied' : 'Copy log'}
          </Button>
        </div>
        <div className="dbx-log" ref={logRef} data-testid="job-log" role="log">
          {lines.length ? (
            lines.map((l) => (
              <div key={l.seq} className={`dbx-log-line dbx-log-${l.stream}`} data-seq={l.seq}>
                <span className="dbx-log-at">{new Date(l.at).toLocaleTimeString()}</span>
                <span className="dbx-log-text">{l.line}</span>
              </div>
            ))
          ) : (
            <div className="dbx-log-line dbx-log-info">
              {mode === 'done' ? 'No output.' : 'Waiting for output…'}
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
